import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Users, Plus, UserPlus, Loader2, Copy, Check, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";

export default function PrivateGroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [inviteBoxOpen, setInviteBoxOpen] = useState(null); // group id
  const [inviteHandle, setInviteHandle] = useState("");
  const [copied, setCopied] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/private-groups/mine");
      setGroups(data);
    } catch { setGroups([]); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.name.trim().length < 2) return toast.error("Group name is too short.");
    setBusy(true);
    try {
      await api.post("/private-groups", { name: form.name.trim(), description: form.description.trim() });
      toast.success("Group created.");
      setCreating(false); setForm({ name: "", description: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create."); } finally { setBusy(false); }
  };

  const invite = async (gid) => {
    if (!inviteHandle.trim()) return;
    try {
      const { data } = await api.post(`/private-groups/${gid}/invite`, { handle: inviteHandle.replace(/^@/, "").trim() });
      toast.success(data.already_member ? "Already a member." : `Invited @${data.invited}!`);
      setInviteHandle(""); setInviteBoxOpen(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not invite."); }
  };

  const copyInvite = async (code) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/register?g=${code}`);
      setCopied(code);
      setTimeout(() => setCopied(null), 1200);
    } catch { toast.error("Could not copy."); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="private-groups-root">
      <header className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-violet-400 mb-1">Fan Clubs</div>
          <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
            <Users className="text-violet-400" size={28} /> Private Groups
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Create fan clubs · share invite links · keep it tight.</p>
        </div>
        <Button onClick={() => setCreating((v) => !v)} className="bg-violet-600 hover:bg-violet-500 text-white rounded-full h-9 px-4 flex items-center gap-2" data-testid="group-new-toggle">
          <Plus size={14} /> {creating ? "Cancel" : "New group"}
        </Button>
      </header>

      {creating && (
        <div className="card-surface p-5 mb-5 space-y-3" data-testid="group-form">
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value.slice(0, 60) }))} placeholder="Group name" className="bg-black/40 border-white/10 rounded-none" data-testid="group-name" />
          <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value.slice(0, 280) }))} placeholder="What's this group about? (optional)" rows={2} className="bg-black/40 border-white/10 rounded-none" data-testid="group-description" />
          <Button onClick={create} disabled={busy} className="bg-violet-600 hover:bg-violet-500 text-white rounded-full w-full" data-testid="group-submit">
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Create group
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-zinc-500">Loading…</div>
      ) : groups.length === 0 ? (
        <div className="card-surface p-10 text-center">
          <div className="font-display text-2xl mb-2">No groups yet.</div>
          <div className="text-sm text-zinc-500">Tap "New group" to make your first fan club.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.id} className="card-surface p-4" data-testid={`group-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link
                    to={`/private-groups/${g.id}`}
                    data-testid={`group-open-${g.id}`}
                    className="text-lg font-semibold leading-tight hover:text-rose-400 transition-colors inline-flex items-center gap-1.5"
                  >
                    {g.name}
                    <MessageSquare size={14} className="text-zinc-500" />
                  </Link>
                  {g.description && <div className="text-sm text-zinc-400 mt-1">{g.description}</div>}
                  <div className="text-xs text-zinc-500 mt-2 flex items-center gap-3">
                    <span>{g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                    {g.is_owner && <span className="text-violet-400 uppercase tracking-widest">Owner</span>}
                  </div>
                </div>
                <div className="shrink-0 flex flex-col gap-1.5">
                  <Button onClick={() => setInviteBoxOpen(inviteBoxOpen === g.id ? null : g.id)} className="bg-violet-600/20 border border-violet-500/40 text-violet-300 hover:bg-violet-600/30 rounded-full h-8 px-3 text-xs" data-testid={`group-invite-${g.id}`}>
                    <UserPlus size={12} className="mr-1" /> Invite
                  </Button>
                  <Button onClick={() => copyInvite(g.invite_code)} variant="ghost" className="rounded-full h-8 px-3 text-xs border border-white/15" data-testid={`group-copy-${g.id}`}>
                    {copied === g.invite_code ? <Check size={12} /> : <Copy size={12} />}
                  </Button>
                </div>
              </div>
              {inviteBoxOpen === g.id && (
                <div className="mt-3 flex gap-2">
                  <Input value={inviteHandle} onChange={(e) => setInviteHandle(e.target.value)} placeholder="@handle to invite" className="bg-black/40 border-white/10 rounded-none flex-1" data-testid={`group-invite-input-${g.id}`} />
                  <Button onClick={() => invite(g.id)} className="bg-violet-600 hover:bg-violet-500 text-white rounded-full h-9 px-4" data-testid={`group-invite-submit-${g.id}`}>Send</Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
